import React, { useEffect, useMemo, useState } from 'react'
import { Flex, Spin } from 'antd'

import { PageLayout } from '@/shared/ui/layout'
import { WialonTokenSection } from './components/WialonTokenSection'
import { SubPartnersCard } from './components/SubPartnersCard'
import { AccountSelectionCard } from './components/AccountSelectionCard'
import { TransferModeCard } from './components/TransferModeCard'
import { DataTransferCard } from './components/DataTransferCard'
import { TransferKey } from 'antd/es/transfer/interface'
import { Account } from '@/widgets/accountsGrid'
import { getSourceApi } from '@/shared/api/config'
import { useAuthStore } from '@/app/store/authStore'
import { useTransferDataForTransfer } from '../model/useTransferDataForTransfer'
import { useAgentsInfoQuery } from '../model/useAgentsInfoQuery'
import { useWialonSourceQueries } from '../model/useWialonSourceQueries'
import { useStartTransfer } from '../model/useStartTransfer'
import { SourceAccount } from '@/app/types/source'
import {
  DEFAULT_MESSAGE_PERIOD,
  TRANSFER_MODES,
  TransferMode,
} from '../model/transferTypes'
import { useSystemStore } from '@/app/store/systemStore'
import { getSystemDisplayName } from '@/shared/lib/getSystemDisplayName'
import { TransferButtonSection } from './components/TransferButtonSetion/TransferButtonSection'
import { SectionCard } from '@/shared/ui/layout/styled'
import { useWialonStore } from '@/app/store/wialonStore'

export const WialonTransferPage: React.FC = () => {
  const { user, userPassword, setTokenAccount, currentServer } = useAuthStore()
  const { selectedSystem } = useSystemStore()
  const systemToken = useWialonStore((s) => s.token)
  const systemUrl = useWialonStore((s) => s.url)
  const [selectedSubpartner, setSelectedSubpartner] = useState('')
  const [userSelectedSubpartner, setUserSelectedSubpartner] = useState(false)
  const [isMyAccountSelected, setIsMyAccountSelected] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState('')
  const [transferMode, setTransferMode] = useState<TransferMode>(
    TRANSFER_MODES.ENTITIES
  )
  const [objectsTargetKeys, setObjectsTargetKeys] = useState<TransferKey[]>([])
  const [usersTargetKeys, setUsersTargetKeys] = useState<TransferKey[]>([])
  const [loadMessages, setLoadMessages] = useState(false)
  const [loadDriversCodes, setLoadDriversCodes] = useState(false)

  const [retranslationsTargetKeys, setRetranslationsTargetKeys] = useState<
    TransferKey[]
  >([])

  const [accountsTargetKeys, setAccountsTargetKeys] = useState<TransferKey[]>(
    []
  )

  const [geozonesTargetKeys, setGeozonesTargetKeys] = useState<TransferKey[]>(
    []
  )
  const [driversTargetKeys, setDriversTargetKeys] = useState<TransferKey[]>([])

  const [loadMessagesFull, setLoadMessagesFull] = useState(false)
  const [messagePeriod, setMessagePeriod] = useState(DEFAULT_MESSAGE_PERIOD)
  const [messagePeriodFull, setMessagePeriodFull] = useState(
    DEFAULT_MESSAGE_PERIOD
  )

  const [targetAgentAccounts, setTargetAgentAccounts] = useState<Account[]>([])

  const sourceApiUrl = getSourceApi(selectedSystem)

  const targetAgentsInfoQuery = useAgentsInfoQuery({
    username: user?.username,
    userPassword,
    currentServer,
  })

  useEffect(() => {
    if (targetAgentsInfoQuery.data)
      setTargetAgentAccounts(targetAgentsInfoQuery.data)
  }, [targetAgentsInfoQuery.data])

  const handleGeozonesChange = (newTargetKeys: TransferKey[]) => {
    setGeozonesTargetKeys(newTargetKeys)
  }

  const handleDriversChange = (newTargetKeys: TransferKey[]) => {
    setDriversTargetKeys(newTargetKeys)
  }

  const handleSelectAllGeozones = () => {
    const dataToUse = geozonesDataForTransfer
    if (dataToUse.length > 0) {
      setGeozonesTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllGeozones = () => {
    setGeozonesTargetKeys([])
  }

  const handleSelectAllDrivers = () => {
    const dataToUse = driversDataForTransfer
    if (dataToUse.length > 0) {
      setDriversTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllDrivers = () => {
    setDriversTargetKeys([])
  }

  const handleAccountsChange = (newTargetKeys: TransferKey[]) => {
    setAccountsTargetKeys(newTargetKeys)
  }

  const handleSelectAllAccounts = () => {
    const dataToUse = accountsDataForTransfer
    if (dataToUse.length > 0) {
      setAccountsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllAccounts = () => {
    setAccountsTargetKeys([])
  }

  const {
    agents: accountsData,
    vehicles: vehiclesData,
    drivers: driversData,
    users: usersData,
    geozones: geosData,
    retranslations: retranslationsData,
    isLoading: isLoadingSourceData,
  } = useWialonSourceQueries(systemToken, sourceApiUrl, systemUrl)

  const subPartners = useMemo(() => {
    if (accountsData.length === 0) return []
    const mainAccountId = accountsData[0].id
    return accountsData.filter((account) => account.id !== mainAccountId)
  }, [accountsData])

  useEffect(() => {
    if (accountsData.length > 0) {
      setTokenAccount(accountsData[0].name || 'Wialon User')
    }
  }, [accountsData])

  const {
    objectsDataForTransfer,
    usersDataForTransfer,
    geozonesDataForTransfer,
    driversDataForTransfer,
    accountsDataForTransfer,
    retranslationsDataForTransfer,
  } = useTransferDataForTransfer<SourceAccount>({
    vehiclesData,
    driversData,
    usersData,
    geosData,
    accountsData,
    retranslationsData,
    selectedSubpartner,
    userSelectedSubpartner,
    transferMode,
    shouldFilterByParentId: true,
  })

  useEffect(() => {
    setUserSelectedSubpartner(false)
    setSelectedSubpartner('')
  }, [systemToken])

  useEffect(() => {
    if (accountsData.length > 0 && !selectedSubpartner) {
      const mainAccountId = accountsData[0].id
      setSelectedSubpartner(mainAccountId)
      setUserSelectedSubpartner(true)
    }
  }, [accountsData, selectedSubpartner])

  useEffect(() => {
    setObjectsTargetKeys([])
    setUsersTargetKeys([])
    setGeozonesTargetKeys([])
    setDriversTargetKeys([])
    setRetranslationsTargetKeys([])
    if (transferMode === TRANSFER_MODES.FULL) {
      setAccountsTargetKeys([])
    }
  }, [selectedSubpartner, transferMode])

  const handleObjectsChange = (newTargetKeys: TransferKey[]) => {
    setObjectsTargetKeys(newTargetKeys)
  }

  const handleUsersChange = (newTargetKeys: TransferKey[]) => {
    setUsersTargetKeys(newTargetKeys)
  }

  const handleSelectAllObjects = () => {
    const dataToUse = objectsDataForTransfer
    if (dataToUse.length > 0) {
      setObjectsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllObjects = () => {
    setObjectsTargetKeys([])
  }

  const handleSelectAllUsers = () => {
    const dataToUse = usersDataForTransfer
    if (dataToUse.length > 0) {
      setUsersTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllUsers = () => {
    setUsersTargetKeys([])
  }

  const handleLoadMessagesChange = (checked: boolean) => {
    setLoadMessages(checked)
  }

  const handleMessagePeriodChange = (period: number) => {
    setMessagePeriod(period)
  }

  const handleLoadDriversCodes = (checked: boolean) => {
    setLoadDriversCodes(checked)
  }

  const handleLoadMessagesFullChange = (checked: boolean) => {
    setLoadMessagesFull(checked)
  }

  const handleMessagePeriodFullChange = (period: number) => {
    setMessagePeriodFull(period)
  }

  const handleRetranslationsChange = (newTargetKeys: TransferKey[]) => {
    setRetranslationsTargetKeys(newTargetKeys)
  }

  const handleSelectAllRetranslations = () => {
    const dataToUse = retranslationsDataForTransfer
    if (dataToUse.length > 0) {
      setRetranslationsTargetKeys(dataToUse.map((item) => item.id))
    }
  }

  const handleDeselectAllRetranslations = () => {
    setRetranslationsTargetKeys([])
  }

  const buildSourceAuth = () => {
    return { auth: systemToken }
  }

  const { handleStartTransfer, isTransferring } = useStartTransfer({
    transferMode,
    isMyAccountSelected,
    selectedAccount,
    targetAgentAccounts,
    selectedSubpartner,
    accountsData,
    subPartners,
    accountsTargetKeys,
    usersTargetKeys,
    objectsTargetKeys,
    driversTargetKeys,
    geozonesTargetKeys,
    retranslationsTargetKeys,
    loadMessages,
    messagePeriod,
    loadMessagesFull,
    messagePeriodFull,
    loadDriversCodes,
    systemUrl,
    buildSourceAuth,
  })

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccount(accountId)
    setIsMyAccountSelected(false)
  }

  const handleMyAccountSelect = (checked: boolean) => {
    setIsMyAccountSelected(checked)
    if (checked) {
      setSelectedAccount('')
    }
  }

  const handleTransferModeChange = (mode: TransferMode) => {
    setTransferMode(mode)
  }

  const handlePartnerSelect = (partnerId: string) => {
    setSelectedSubpartner(partnerId)
    setUserSelectedSubpartner(true)
  }

  const isAccountNotSelected = !selectedAccount && !isMyAccountSelected
  const isButtonDisabled = isAccountNotSelected || isTransferring

  return (
    <PageLayout
      title={`Перенос данных из ${getSystemDisplayName(selectedSystem)} в ГЛОНАССSoft`}
      subtitle="Настройте параметры для переноса объектов, пользователей и сообщений"
    >
      <Flex vertical gap={32}>
        <div>
          <WialonTokenSection />
          <SubPartnersCard
            partners={subPartners}
            selectedPartner={selectedSubpartner}
            onPartnerSelect={handlePartnerSelect}
          />
        </div>

        <Spin spinning={isTransferring}>
          <AccountSelectionCard
            selectedAccount={selectedAccount}
            onAccountSelect={handleAccountSelect}
            isLoading={targetAgentsInfoQuery.isLoading}
            accounts={targetAgentAccounts}
            isMyAccountSelected={isMyAccountSelected}
            onMyAccountSelect={handleMyAccountSelect}
          />
        </Spin>

        <Spin spinning={isTransferring}>
          <TransferModeCard
            transferMode={transferMode}
            onTransferModeChange={handleTransferModeChange}
          />
        </Spin>

        <SectionCard>
          <DataTransferCard
            isLoading={isLoadingSourceData}
            transferMode={transferMode}
            // Для режима "Только сущности"
            objectsTargetKeys={objectsTargetKeys}
            usersTargetKeys={usersTargetKeys}
            geozonesTargetKeys={geozonesTargetKeys}
            driversTargetKeys={driversTargetKeys}
            loadMessages={loadMessages}
            loadDriversCodes={loadDriversCodes}
            messagePeriod={messagePeriod}
            retranslationsTargetKeys={retranslationsTargetKeys}
            // Для режима "Полный перенос"
            accountsTargetKeys={accountsTargetKeys}
            loadMessagesFull={loadMessagesFull}
            messagePeriodFull={messagePeriodFull}
            // Обработчики для режима "Только сущности"
            onObjectsChange={handleObjectsChange}
            onUsersChange={handleUsersChange}
            onGeozonesChange={handleGeozonesChange}
            onDriversChange={handleDriversChange}
            onSelectAllObjects={handleSelectAllObjects}
            onDeselectAllObjects={handleDeselectAllObjects}
            onSelectAllUsers={handleSelectAllUsers}
            onDeselectAllUsers={handleDeselectAllUsers}
            onSelectAllGeozones={handleSelectAllGeozones}
            onDeselectAllGeozones={handleDeselectAllGeozones}
            onSelectAllDrivers={handleSelectAllDrivers}
            onDeselectAllDrivers={handleDeselectAllDrivers}
            onLoadMessagesChange={handleLoadMessagesChange}
            onMessagePeriodChange={handleMessagePeriodChange}
            onLoadDriversCodesChange={handleLoadDriversCodes}
            onRetranslationsChange={handleRetranslationsChange}
            onSelectAllRetranslations={handleSelectAllRetranslations}
            onDeselectAllRetranslations={handleDeselectAllRetranslations}
            // Обработчики для режима "Полный перенос"
            onAccountsChange={handleAccountsChange}
            onSelectAllAccounts={handleSelectAllAccounts}
            onDeselectAllAccounts={handleDeselectAllAccounts}
            onLoadMessagesFullChange={handleLoadMessagesFullChange}
            onMessagePeriodFullChange={handleMessagePeriodFullChange}
            // Данные для передачи
            objectsData={objectsDataForTransfer}
            usersData={usersDataForTransfer}
            geozonesData={geozonesDataForTransfer}
            driversData={driversDataForTransfer}
            accountsData={accountsDataForTransfer}
            retranslationsData={retranslationsDataForTransfer}
          />

          <TransferButtonSection
            transferMode={transferMode}
            disabled={isButtonDisabled || isTransferring}
            onClick={() => handleStartTransfer()}
            loading={isTransferring}
            objectsTargetKeys={objectsTargetKeys}
            usersTargetKeys={usersTargetKeys}
            geozonesTargetKeys={geozonesTargetKeys}
            driversTargetKeys={driversTargetKeys}
            retranslationsTargetKeys={retranslationsTargetKeys}
            accountsTargetKeys={accountsTargetKeys}
            text={
              isTransferring ? 'Выполняется перенос...' : 'Запустить перенос'
            }
          />
        </SectionCard>
      </Flex>
    </PageLayout>
  )
}
